import {
  addDoc,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { firebaseAuth, firebaseDb } from '../firebase';
import { paymentApiClient } from './apiClient';

async function getProfileRole() {
  const u = firebaseAuth?.currentUser;
  if (!u || !firebaseDb) return null;
  const snap = await getDoc(doc(firebaseDb, 'users', u.uid));
  if (!snap.exists()) return null;
  return snap.data().role || null;
}

export const paymentService = {
  /**
   * Créer un lien de paiement Stripe
   */
  createStripePayment: async (orderId) => {
    return {
      success: false,
      error:
        'Paiement: à implémenter via Cloud Functions (pas d’API Express en mode Firebase-only).',
    };
  },

  /**
   * Créer un paiement Mobile Money via SerdiPay (API Cloud Functions).
   */
  createMobileMoneyPayment: async (orderId, phoneNumber, provider = 'orange') => {
    try {
      const response = await paymentApiClient.post(`/payment/mobile-money/${orderId}`, {
        phoneNumber,
        provider,
      });
      return {
        success: true,
        ...response.data,
      };
    } catch (error) {
      const details =
        error.response?.data?.details ||
        error.response?.data?.error ||
        error.message;
      return {
        success: false,
        error: details || 'Erreur lors de la création du paiement Mobile Money',
      };
    }
  },

  /**
   * Vérifier le statut SerdiPay d'une commande (Firestore, après callback).
   */
  verifySerdiPayPayment: async (orderId) => {
    try {
      const response = await paymentApiClient.get('/payment/serdipay/verify', {
        params: { orderId },
      });
      return {
        success: true,
        ...response.data,
      };
    } catch (error) {
      const details =
        error.response?.data?.details ||
        error.response?.data?.error ||
        error.message;
      return {
        success: false,
        error: details || 'Erreur lors de la vérification du paiement',
      };
    }
  },

  /**
   * Vérifier le statut d'un paiement Stripe
   */
  verifyStripePayment: async (sessionId) => {
    return {
      success: false,
      error:
        'Vérification paiement: à implémenter via Cloud Functions (webhook Stripe + Firestore).',
    };
  },

  /**
   * Simuler un paiement réussi (agents/admins uniquement).
   * Met à jour la commande dans Firestore et envoie une notification au client.
   */
  simulatePayment: async (orderId) => {
    try {
      if (!firebaseAuth?.currentUser || !firebaseDb) {
        return {
          success: false,
          error: 'Non authentifié ou Firestore indisponible.',
        };
      }

      const role = await getProfileRole();
      if (!['agent', 'admin'].includes(role)) {
        return {
          success: false,
          error: 'Action réservée aux agents ou administrateurs.',
        };
      }

      const orderRef = doc(firebaseDb, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      if (!orderSnap.exists()) {
        return { success: false, error: 'Commande introuvable.' };
      }

      const order = orderSnap.data();
      const amount = Number(order.finalAmount ?? order.totalAmount) || 0;
      if (amount <= 0) {
        return {
          success: false,
          error:
            'Impossible de simuler : aucun montant sur cette commande (prix non défini par un agent).',
        };
      }
      if (order.paymentStatus === 'Payé') {
        return { success: false, error: 'Cette commande est déjà marquée comme payée.' };
      }

      await updateDoc(orderRef, {
        paymentStatus: 'Payé',
        paymentSimulated: true,
        paidAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const clientUid = order.userUid || order.userId;
      if (clientUid) {
        try {
          await addDoc(collection(firebaseDb, 'users', clientUid, 'notifications'), {
            title: 'Paiement enregistré (simulation)',
            body: `Votre commande est marquée comme payée. Montant : $${amount.toFixed(2)}`,
            read: false,
            type: 'payment_simulated',
            orderId: String(orderId),
            createdAt: serverTimestamp(),
          });
        } catch (notifyErr) {
          console.warn('Notification après paiement simulé:', notifyErr);
        }
      }

      const fresh = await getDoc(orderRef);
      return {
        success: true,
        message: 'Paiement simulé avec succès.',
        order: { id: fresh.id, _id: fresh.id, ...fresh.data() },
      };
    } catch (e) {
      console.error('simulatePayment:', e);
      return {
        success: false,
        error: e.message || 'Erreur lors de la simulation du paiement.',
      };
    }
  },
};
